import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCw, Package, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const BLOCKS = [
  ["theNightBefore", "The night before"],
  ["raceMorning", "Race morning"],
  ["duringTheRace", "During the race"],
  ["afterwards", "Afterwards"],
];

// Smart race-day fuelling brief — built from the flagged race session, the
// athlete's weight, dietary exclusions and their plan targets.
export default function AiRaceBrief({ onBrief, planId, durMin, numbers }) {
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);

  const take = (d) => { setBrief(d); onBrief?.(d); };

  useEffect(() => {
    const q = planId ? `?planId=${encodeURIComponent(planId)}` : "";
    api.get(`/ai/race-brief/latest${q}`)
      .then(({ data }) => {
        // A brief written for a different race length is worse than none.
        const stale = data?.durMin && durMin && Math.abs(data.durMin - durMin) > 15;
        take(data && data.raceMorning && !stale ? data : null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, durMin]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/race-brief", { planId, durMin, ...(numbers || {}) });
      // Same guard as on load — a brief for a different race length is noise.
      const stale = data?.durMin && durMin && Math.abs(data.durMin - durMin) > 15;
      if (stale) {
        toast.error("That brief was written for a different race length — try again");
      } else {
        take(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build your brief");
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-white/10 bg-[#141414] p-5" data-testid="ai-race-brief">
      <div className="flex items-start gap-3">
        <div className="grid place-items-center h-9 w-9 shrink-0 border border-[#A78BFA]/40 bg-[#A78BFA]/10">
          <Sparkles className="h-4 w-4 text-[#A78BFA]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Smart race brief</p>
          <h3 className="font-display text-lg uppercase tracking-tight leading-tight mt-1" data-testid="race-brief-headline">
            {brief ? brief.headline : "Your race plan, written for you"}
          </h3>
        </div>
        <Button data-testid="race-brief-generate-btn" onClick={generate} disabled={busy}
          className="rounded-none shrink-0 bg-[#A78BFA] hover:bg-[#8f6ff5] text-black text-[11px] font-bold uppercase tracking-wider">
          {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Writing…</>
                : brief ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</> : "Generate"}
        </Button>
      </div>

      {!brief && !busy && (
        <p className="text-sm text-zinc-300 mt-3">Get a personalised brief — night-before, race-morning timings, carbs per hour, what to carry and what to do if your stomach turns. Built from your race, weight and exclusions.</p>
      )}

      {brief && (
        <div className="mt-4 space-y-4">
          {BLOCKS.filter(([k]) => brief[k]).map(([k, label]) => (
            <div key={k} data-testid={`race-brief-${k}`}>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#E2E30A] font-bold mb-1">{label}</p>
              <p className="text-sm text-zinc-100 leading-relaxed">{String(brief[k]).replace(/\*\*/g, "")}</p>
            </div>
          ))}

          {(brief.carry || []).length > 0 && (
            <div className="border border-white/10 bg-black/40 p-3" data-testid="race-brief-carry">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold flex items-center gap-1.5 mb-2">
                <Package className="h-3.5 w-3.5 text-[#4C9AFF]" /> Carry with you
              </p>
              <ul className="space-y-1">
                {brief.carry.map((c, i) => <li key={i} className="text-[13px] text-zinc-200">— {c}</li>)}
              </ul>
            </div>
          )}

          {(brief.contingency || []).length > 0 && (
            <div className="border border-[#FF9500]/25 bg-[#FF9500]/[0.05] p-3" data-testid="race-brief-contingency">
              <p className="text-[10px] uppercase tracking-wider text-[#FF9500] font-bold flex items-center gap-1.5 mb-2">
                <ShieldAlert className="h-3.5 w-3.5" /> If it goes wrong
              </p>
              <ul className="space-y-1">
                {brief.contingency.map((c, i) => <li key={i} className="text-[13px] text-zinc-200">— {c}</li>)}
              </ul>
            </div>
          )}
          <p className="text-[10px] text-zinc-500">Smart guidance — nothing new on race day. Not medical advice.</p>
        </div>
      )}
    </section>
  );
}
